import EditIcon from "@mui/icons-material/Edit";
import { useMemo, useRef, useState } from "react";
import { availableTokens, EMAIL_FIELD_LIMITS } from "@/constants/campaignBuilder";
import { LANDING_FIELD_LIMITS } from "@/constants/landing";
import { type LandingSettings, useCampaignBuilder } from "@/context/CampaignBuilderContext";
import { useSubjectAtMention } from "@/hooks/useSubjectAtMention";
import { isAnonymousLinks } from "@/utils/campaignMode-helper";
import { insertAtCursor } from "@/utils/messagePanel-helper";
import { PillToggle } from "../PillToggle";
import { TokenDropdown } from "../TokenDropdown";
import { TokenMenu } from "../TokenMenu";
import { FieldGroup } from "../unboxing/FieldGroup";
import { FieldLabel } from "../unboxing/FieldLabel";

interface LandingMessagePanelProps {
  state: LandingSettings;
  onChange: (v: Partial<LandingSettings>) => void;
}

const INPUT_CLASS =
  "w-full h-10 px-3.5 border rounded-btn bg-white font-lato text-[14px] text-rodeo-black placeholder:text-graphite outline-none transition-colors border-smoke focus:border-dark-dust";
const TEXTAREA_CLASS =
  "w-full px-3.5 py-2.5 border rounded-btn bg-white font-lato text-[14px] text-rodeo-black placeholder:text-graphite outline-none transition-colors border-smoke focus:border-dark-dust resize-y";

/** Same chrome as the email MessagePanel's section edit button. */
const editBtnStyle: React.CSSProperties = {
  background: "transparent",
  border: "1px solid var(--color-smoke)",
  borderRadius: "4px",
  padding: "3px",
  lineHeight: "1",
  cursor: "pointer",
  fontSize: "16px",
};

type LandingMessageSection = "headline" | "subtitle";

function SectionEditButton({
  expanded,
  onClick,
  editTitle,
}: {
  expanded: boolean;
  onClick: () => void;
  editTitle: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-dark-dust hover:text-rodeo-black transition-colors"
      style={editBtnStyle}
      title={expanded ? "Close" : editTitle}
      aria-expanded={expanded}
    >
      <EditIcon sx={{ fontSize: 16 }} />
    </button>
  );
}

/**
 * Landing → Message tab. Edits the hero headline + subtitle, the landing video, and
 * the gift message. Gift message is shared with the Unboxing tab (both read/write
 * `stage3Unboxing`), so a change here reflects there and vice-versa — matching the
 * prototype's mirrored Gift Message control.
 *
 * Headline and Subtitle open collapsed (label + pencil only) and reveal their input
 * on click, like the email Message and Reminder panels. Both are personalizable:
 * the "Personalize with tags" dropdown inserts a token at the caret, and typing
 * `@` inside the field opens the same list inline.
 */
export default function LandingMessagePanel({ state, onChange }: LandingMessagePanelProps) {
  const { stage3Unboxing, setStage3Unboxing, notificationMode, linkToList } = useCampaignBuilder();

  // Only ANONYMOUS links can't resolve @FirstName / @LastName — manual "No, just set a quantity"
  // (Path A) and every universal campaign. A manual "link to a list" campaign (Path B) has named
  // recipients, exactly like gfg, so it keeps those tags. Drop them for anonymous links — see availableTokens.
  const anonymousLinks = isAnonymousLinks(notificationMode, linkToList);
  const tokens = useMemo(() => availableTokens(anonymousLinks), [anonymousLinks]);
  const [expandedSections, setExpandedSections] = useState<Record<LandingMessageSection, boolean>>({
    headline: false,
    subtitle: false,
  });

  const toggleSection = (section: LandingMessageSection) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const headlineRef = useRef<HTMLInputElement>(null);
  const subtitleRef = useRef<HTMLTextAreaElement>(null);

  const headlineMention = useSubjectAtMention(
    headlineRef,
    (v) => onChange({ headline: v }),
    LANDING_FIELD_LIMITS.headline,
    tokens,
  );
  const subtitleMention = useSubjectAtMention(
    subtitleRef,
    (v) => onChange({ subtitle: v }),
    LANDING_FIELD_LIMITS.subtitle,
    tokens,
  );

  return (
    <div style={{ padding: 20 }}>
      {/* Headline */}
      <FieldGroup>
        <FieldLabel>
          <span className="flex items-center">
            Headline
            {expandedSections.headline && (
              <TokenDropdown
                tokens={tokens}
                onInsert={(token) => {
                  const el = headlineRef.current;
                  if (el)
                    insertAtCursor(
                      el,
                      token,
                      (v) => onChange({ headline: v }),
                      LANDING_FIELD_LIMITS.headline,
                    );
                }}
              />
            )}
          </span>
          <SectionEditButton
            expanded={expandedSections.headline}
            onClick={() => toggleSection("headline")}
            editTitle="Edit headline"
          />
        </FieldLabel>
        {expandedSections.headline && (
          <div className="relative">
            <input
              ref={headlineRef}
              type="text"
              value={state.headline}
              maxLength={LANDING_FIELD_LIMITS.headline}
              onChange={headlineMention.handleSubjectChange}
              onKeyDown={headlineMention.handleSubjectKeyDown}
              onBlur={headlineMention.dismissAtMention}
              autoComplete="off"
              placeholder="Your Gift that Gives Back"
              className={INPUT_CLASS}
            />
            {headlineMention.subjectAtMention.active &&
              headlineMention.subjectAtMention.items.length > 0 && (
                <TokenMenu
                  items={headlineMention.subjectAtMention.items}
                  activeIdx={headlineMention.subjectAtMention.activeIdx}
                  onInsert={headlineMention.insertSubjectToken}
                  className="absolute left-0 z-50"
                  style={{ top: "calc(100% + 4px)" }}
                />
              )}
          </div>
        )}
      </FieldGroup>

      {/* Subtitle */}
      <FieldGroup>
        <FieldLabel>
          <span className="flex items-center">
            Subtitle
            {expandedSections.subtitle && (
              <TokenDropdown
                tokens={tokens}
                onInsert={(token) => {
                  const el = subtitleRef.current;
                  if (el)
                    insertAtCursor(
                      el,
                      token,
                      (v) => onChange({ subtitle: v }),
                      LANDING_FIELD_LIMITS.subtitle,
                    );
                }}
              />
            )}
          </span>
          <SectionEditButton
            expanded={expandedSections.subtitle}
            onClick={() => toggleSection("subtitle")}
            editTitle="Edit subtitle"
          />
        </FieldLabel>
        {expandedSections.subtitle && (
          <>
            <div className="relative">
              <textarea
                ref={subtitleRef}
                value={state.subtitle}
                maxLength={LANDING_FIELD_LIMITS.subtitle}
                rows={3}
                onChange={subtitleMention.handleSubjectChange}
                onKeyDown={subtitleMention.handleSubjectKeyDown}
                onBlur={subtitleMention.dismissAtMention}
                placeholder="Every gift in this collection helps people or the planet."
                className={TEXTAREA_CLASS}
              />
              {subtitleMention.subjectAtMention.active &&
                subtitleMention.subjectAtMention.items.length > 0 && (
                  <TokenMenu
                    items={subtitleMention.subjectAtMention.items}
                    activeIdx={subtitleMention.subjectAtMention.activeIdx}
                    onInsert={subtitleMention.insertSubjectToken}
                    className="absolute left-0 z-50"
                    style={{ top: "calc(100% + 4px)" }}
                  />
                )}
            </div>
            <div className="flex justify-end mt-1">
              <span className="font-lato text-[11px] text-graphite">
                {state.subtitle.length} / {LANDING_FIELD_LIMITS.subtitle}
              </span>
            </div>
          </>
        )}
      </FieldGroup>

      <div style={{ borderTop: "1px solid var(--color-smoke)", margin: "8px 0 20px" }} />

      {/* Gift Message — synced with the Unboxing tab */}
      <FieldGroup>
        <FieldLabel>
          Gift Message
          <PillToggle
            checked={stage3Unboxing.showGiftMessage}
            onChange={(v) => setStage3Unboxing({ showGiftMessage: v })}
            aria-label="Show gift message"
          />
        </FieldLabel>
        {stage3Unboxing.showGiftMessage && (
          <>
            <textarea
              value={stage3Unboxing.giftMessageText}
              maxLength={EMAIL_FIELD_LIMITS.message}
              rows={4}
              onChange={(e) => setStage3Unboxing({ giftMessageText: e.target.value })}
              placeholder="This message appears when recipients tap Gift Message on the landing page."
              className={TEXTAREA_CLASS}
            />
            <div className="flex justify-between mt-1">
              <span className="font-lato text-[11px] text-graphite">
                Synced with the Unboxing tab
              </span>
              <span className="font-lato text-[11px] text-graphite">
                {stage3Unboxing.giftMessageText.length} / {EMAIL_FIELD_LIMITS.message}
              </span>
            </div>
          </>
        )}
      </FieldGroup>

      {/* Video */}
      <FieldGroup>
        <FieldLabel>
          Video
          <PillToggle
            checked={state.showVideo}
            onChange={(v) => onChange({ showVideo: v })}
            aria-label="Show video message"
          />
        </FieldLabel>
        {state.showVideo && (
          <>
            <input
              type="url"
              value={state.videoUrl}
              onChange={(e) => onChange({ videoUrl: e.target.value })}
              placeholder="Paste a YouTube or Vimeo link"
              className={INPUT_CLASS}
            />
            <p className="mt-1.5 font-lato text-[12px] text-graphite leading-snug">
              Add a link to personalize your video.
            </p>
          </>
        )}
      </FieldGroup>
    </div>
  );
}
